import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { voidJournal, postJournal } from '../../api/journal';
import { ACCOUNTS, rateFor, costPayableAccount, supplierTypesFor } from '../../api/accounts';
import { getClientDepositBalance, applyClientDeposit, getInvoicePaidAmount } from '../../api/receiptAllocations';
import { useAuth } from '../../context/AuthContext';
import PackageTerms from '../../components/print/PackageTerms';
import PrintFooter from '../../components/print/PrintFooter';
import PaymentDetails from '../../components/print/PaymentDetails';
import { usePasscode } from '../../components/common/PasscodeModal';
import ApplyDepositModal from '../../components/common/ApplyDepositModal';
import SupplierPicker from '../../components/common/SupplierPicker';
import { costPaxQty, lineTypeOf } from '../../utils/costPax';
import { downloadElementAsPdf } from '../../utils/downloadPdf';

// Which revenue account an invoice's sell side posts to, by source type — shared between
// saveAdminEdit() (repost after a full correction) and submitPartialRefund() (partial
// credit note), so both money-changing actions on an invoice agree on the same account.
// Must match InvoiceNew.js REVENUE_ACCOUNTS, or a re-edit moves revenue to a different account.
const REVENUE_MAP = {
  ticket:  ACCOUNTS.TICKET_REVENUE,
  visa:    ACCOUNTS.VISA_REVENUE,
  booking: ACCOUNTS.BOOKING_REVENUE,
};

// Cost side, by source type — must match InvoiceNew.js COST_ACCOUNTS so an admin re-edit
// re-posts supplier costs to the same COGS / AP accounts the invoice was created with.
const COST_MAP = {
  ticket:  { expense: ACCOUNTS.TICKET_COST_LKR, payable: ACCOUNTS.AIRLINE_PAYABLE },
  visa:    { expense: ACCOUNTS.VISA_COST,       payable: ACCOUNTS.VISA_PAYABLE },
  booking: { expense: ACCOUNTS.HOTEL_LAND_COST, payable: ACCOUNTS.HOTEL_LAND_PAYABLE },
};

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canSee, role, agentRefId } = useAuth();
  const isLimitedAgent = role === 'agent'; // an admin who also happens to be an agent (e.g. Lakmali) keeps full admin access
  const username = user?.email?.split('@')[0] || 'unknown';
  const printRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adminEditing, setAdminEditing] = useState(false);
  const [editForm, setEditForm] = useState({ due_date: '', notes: '', line_items: [] });
  const [paxTypes, setPaxTypes] = useState([]);
  useEffect(() => {
    supabase.from('pax_types').select('name').eq('is_active', true).eq('is_common', false).order('sort_order')
      .then(({ data }) => setPaxTypes((data || []).map(t => t.name)));
  }, []);
  const [advances, setAdvances] = useState([]);
  const [depositBalance, setDepositBalance] = useState(0);
  const [showApplyDeposit, setShowApplyDeposit] = useState(false);
  const [applyingDeposit, setApplyingDeposit] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refundError, setRefundError] = useState('');
  const [refundCommissionWarning, setRefundCommissionWarning] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [packageGpTiers, setPackageGpTiers] = useState([]);
  const [paidToDate, setPaidToDate] = useState(0);
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  useEffect(() => { if (id) load(); }, [id]);

  async function load() {
    setLoading(true);
    const { data: inv } = await supabase
      .from('invoices')
      .select('*, clients(full_name, client_code, email, phone, address, care_of_agent_id, agents:care_of_agent_id(tls_code, full_name))')
      .eq('id', id)
      .single();

    if (!inv) { setLoading(false); return; }
    // A limited agent role can otherwise view ANY invoice by id (no data-level check
    // existed here at all) — block anything that isn't one of their own clients'.
    if (isLimitedAgent && inv.clients?.care_of_agent_id !== agentRefId) {
      setInvoice({ accessDenied: true });
      setLoading(false);
      return;
    }
    setInvoice(inv);

    // Load payments: receipts linked directly to invoice (legacy single-reference or new
    // FIFO allocations), or a deposit application already posted against this invoice.
    const rvQuery = supabase
      .from('receipt_vouchers')
      .select('id, voucher_number, voucher_date, amount, base_amount, payment_mode, currencies(code)')
      .is('voided_at', null);

    let allAdvances = [];
    const [{ data: invReceipts }, { data: invAllocs }, { data: depositApps }] = await Promise.all([
      rvQuery.eq('reference_type', 'invoice').eq('reference_id', inv.id),
      supabase.from('receipt_allocations')
        .select('amount, receipt_vouchers(voucher_number, voucher_date, payment_mode, currencies(code))')
        .eq('invoice_id', inv.id),
      supabase.from('journal_entries')
        .select('entry_date, description, journal_lines(credit_amount, account_id)')
        .eq('reference_type', 'DEPOSIT_APPLICATION').eq('reference_id', inv.id).eq('status', 'POSTED'),
    ]);
    const allocRows = (invAllocs || []).map(a => ({
      voucher_number: a.receipt_vouchers?.voucher_number || 'Commission set-off',
      voucher_date: a.receipt_vouchers?.voucher_date,
      payment_mode: a.receipt_vouchers?.payment_mode || 'Set off against agent commission',
      currencies: a.receipt_vouchers?.currencies,
      amount: a.amount,
      base_amount: a.amount,
    }));
    const depositAppRows = (depositApps || []).map(je => ({
      voucher_number: 'Deposit applied',
      voucher_date: je.entry_date,
      payment_mode: je.description,
      currencies: null,
      amount: (je.journal_lines || []).find(l => l.account_id === ACCOUNTS.AR)?.credit_amount || 0,
      base_amount: (je.journal_lines || []).find(l => l.account_id === ACCOUNTS.AR)?.credit_amount || 0,
    }));
    allAdvances = [...(invReceipts || []), ...allocRows, ...depositAppRows];

    // Legacy fallback: pro forma deposits taken before this invoice was raised, from before
    // deposit applications were posted as their own journal entry. Only used when no real
    // application entry exists yet for this invoice, so we never double-count a deposit that's
    // already been applied properly.
    if (inv.quotation_id && depositAppRows.length === 0) {
      const { data: quotReceipts } = await supabase
        .from('receipt_vouchers')
        .select('voucher_number, voucher_date, amount, base_amount, payment_mode, currencies(code)')
        .eq('reference_type', 'quotation').eq('reference_id', inv.quotation_id)
        .is('voided_at', null);
      allAdvances = [...allAdvances, ...(quotReceipts || [])];
    }
    setAdvances(allAdvances);

    if (inv.client_id) {
      const bal = await getClientDepositBalance(inv.client_id);
      setDepositBalance(bal);
    }

    // Same dual-lookup pattern as openRefund()/CommissionLedger.js — a commission's
    // reference_id can point at either the invoice itself or its source record, since
    // some flows save the commission before an invoice necessarily exists yet.
    const [{ data: comms }, { data: pkgTiers }] = await Promise.all([
      supabase.from('commissions')
        .select('*, agents:agent_ref_id(tls_code, full_name), commission_tables:commission_table_id(rule_name, gp_from, gp_to, commission_percent, fixed_amount, amount_per_unit, commission_basis, commission_type)')
        .in('reference_id', [inv.id, inv.source_id].filter(Boolean))
        .order('created_at', { ascending: false }),
      // Fallback lookup for older package commissions that recorded units + a total
      // commission_amount but never saved which commission_tables row produced it — the
      // convention for a package is a PER-PAX GP tier lookup (matched on gp_amount/units,
      // never on the invoice's total GP), fixed_amount x units. Reconstruct that here so a
      // commission with no live rule reference still shows its real tier, not a guess.
      supabase.from('commission_tables').select('gp_from, gp_to, fixed_amount, rule_name')
        .eq('product_type', 'Package').eq('commission_type', 'GP_TIERED').eq('is_active', true).order('gp_from'),
    ]);
    setCommissions(comms || []);
    setPackageGpTiers(pkgTiers || []);

    // Load source record
    if (inv.source_type === 'booking') {
      const { data } = await supabase
        .from('bookings')
        .select('*, packages(package_name, package_code), sell_currency:sell_currency_id(code)')
        .eq('id', inv.source_id)
        .single();
      setSourceData(data);
      initEditForm(inv, data, 'booking');
    } else if (inv.source_type === 'ticket') {
      const { data } = await supabase
        .from('tickets')
        .select('*, sell_currency:sell_currency_id(code)')
        .eq('id', inv.source_id)
        .single();
      setSourceData(data);
      initEditForm(inv, data, 'ticket');
    } else if (inv.source_type === 'visa') {
      const { data } = await supabase
        .from('visas')
        .select('*, visa_types(name, country), clients(full_name), sell_currency:sell_currency_id(code)')
        .eq('id', inv.source_id)
        .single();
      setSourceData(data);
      initEditForm(inv, data, 'visa');
    } else {
      initEditForm(inv, null, null);
    }

    setLoading(false);
  }

  function initEditForm(inv, src, type) {
    const items = inv.line_items && inv.line_items.length > 0
      ? inv.line_items
      : buildLineItems(type, src);
    setEditForm({
      due_date: inv.due_date || '',
      notes: inv.notes || '',
      invoice_type: inv.invoice_type || 'INVOICE',
      line_items: items.map(it => ({ ...it })),
      passengers: (src?.passengers || []).map(p => ({ ...p })),
      cost_items: (inv.cost_items || []).map(c => ({ ...c })),
    });
  }

  async function updateStatus(status) {
    setUpdating(true);
    if (status === 'VOID') {
      // Reverse ALL journal entries linked to this invoice — revenue, every cost line, AND
      // any deposit applied against it (DEPOSIT_APPLICATION doesn't start with "invoice", so
      // filtering by reference_type prefix used to skip it, orphaning that deposit: the
      // client's deposit pool stayed reduced for an invoice that no longer exists). Filter by
      // reference_id alone — every reference_type that ever points at an invoice's id belongs
      // to that invoice, so nothing legitimately gets excluded.
      // Reversal entries (reversal_of set) are skipped: they already cancel an earlier entry,
      // and reversing one resurrects the original — voiding an invoice that had been edited
      // before left its old costs and AR live again (9 void invoices, ~5.3M).
      const { data: journals } = await supabase.from('journal_entries')
        .select('id, is_reversed, reversal_of')
        .eq('reference_id', id);
      for (const je of (journals || [])) {
        if (!je.is_reversed && !je.reversal_of) {
          await voidJournal(je.id, `Voided invoice ${invoice.invoice_number}`, username);
        }
      }
      // A group-package invoice's "Advance applied" draw-down is tagged to the package, not
      // the invoice (so Admin Edit's invoice-scoped repost doesn't consume the advance
      // twice) — so the sweep above misses it. A void must hand the advance back to the
      // package, otherwise the supplier deposit stays drawn down for an invoice that no
      // longer exists (TLS-26-1246 left 119,000 of IATA's advance consumed this way).
      const { data: advances } = await supabase.from('journal_entries')
        .select('id, is_reversed').eq('reference_type', 'package').is('reversal_of', null)
        .like('description', `Advance applied — ${invoice.invoice_number}%`);
      for (const je of (advances || [])) {
        if (!je.is_reversed) await voidJournal(je.id, `Voided invoice ${invoice.invoice_number}`, username);
      }
      // Fallback: also reverse the stored journal_entry_id if not caught above
      if (invoice.journal_entry_id) {
        const { data: je } = await supabase.from('journal_entries').select('is_reversed').eq('id', invoice.journal_entry_id).single();
        if (je && !je.is_reversed) {
          await voidJournal(invoice.journal_entry_id, `Voided invoice ${invoice.invoice_number}`, username);
        }
      }
    }
    await supabase.from('invoices').update({ status }).eq('id', id);
    setInvoice(f => ({ ...f, status }));
    setUpdating(false);
  }

  async function saveEdit() {
    setUpdating(true);
    // Only non-financial fields — amounts, client, date, currency are locked per accounting standards
    const patch = {
      due_date: editForm.due_date || null,
      notes: editForm.notes || null,
      line_items: editForm.line_items, // descriptions/sub-lines only; amounts unchanged by UI
    };
    await supabase.from('invoices').update(patch).eq('id', id);
    // Save passenger ticket numbers back to the booking
    if (invoice.source_type === 'booking' && invoice.source_id && editForm.passengers?.length) {
      await supabase.from('bookings').update({ passengers: editForm.passengers }).eq('id', invoice.source_id);
      setSourceData(f => ({ ...f, passengers: editForm.passengers }));
    }
    setInvoice(f => ({ ...f, ...patch }));
    setEditing(false);
    setUpdating(false);
  }

  async function reopenCosting() {
    try { await promptPasscode(); } catch { return; }
    setUpdating(true);
    const { error: err } = await supabase.from('invoices').update({ costing_finalized: false }).eq('id', id);
    setUpdating(false);
    if (err) { alert('Failed to reopen costing: ' + err.message); return; }
    setInvoice(f => ({ ...f, costing_finalized: false }));
    navigate('/invoices/costing-pending');
  }

  async function handleAdminEdit() {
    try { await promptPasscode(); } catch { return; }
    // Just enter edit mode — the ledger is untouched until saveAdminEdit() actually commits
    // the correction. Voiding here (as this used to do) meant clicking Cancel afterwards
    // left the invoice with no live journal entry at all.
    setEditing(false);
    setAdminEditing(true);
  }

  async function saveAdminEdit() {
    setUpdating(true);
    try {
      const items = editForm.line_items;
      const newTotal = items.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

      const REF_TYPE_MAP = { ticket: 'INVOICE_TICKET', visa: 'INVOICE_VISA', booking: 'INVOICE_BOOKING' };
      const revenueAcct = REVENUE_MAP[invoice.source_type] || ACCOUNTS.PACKAGE_REVENUE_LKR;
      const refType = REF_TYPE_MAP[invoice.source_type] || 'INVOICE_TICKET';
      const num = invoice.invoice_number;
      const entryDate = editForm.issued_date || invoice.issued_date;

      // Void all live (non-reversal, not-already-reversed) journals for this invoice right
      // before posting the correction, so the void and the repost happen atomically together.
      // reference_type varies by how the entry was posted (creation vs earlier admin-edit) — match by prefix.
      const { data: journals } = await supabase.from('journal_entries')
        .select('id, is_reversed, reversal_of').ilike('reference_type', 'invoice%').eq('reference_id', id);
      for (const oldJe of (journals || [])) {
        if (!oldJe.is_reversed && !oldJe.reversal_of) {
          await voidJournal(oldJe.id, `Admin edit: voiding invoice ${invoice.invoice_number}`, username);
        }
      }

      const je = await postJournal({
        date: entryDate,
        description: `Invoice ${num} (admin re-edit)`,
        reference_type: refType,
        reference_id: id,
        branch_id: invoice.branch_id || null,
        created_by: username,
        lines: [
          { account_id: ACCOUNTS.AR, debit_amount: newTotal, credit_amount: 0, description: `AR - ${num}`, party_type: 'client', party_id: invoice.client_id },
          { account_id: revenueAcct, debit_amount: 0, credit_amount: newTotal, description: `Revenue - ${num}` },
        ],
      });

      // The void above reversed the cost journals too, so every cost row must be re-posted
      // (DR COGS / CR supplier AP) — otherwise the supplier ledger and AP silently lose the
      // cost while the row still reads `journaled: true`. Internal/Tour Operator rows keep
      // their usual deferral until costing is finalized.
      const { expense, payable: defaultPayable } = COST_MAP[invoice.source_type] || COST_MAP.ticket;
      const rows = (editForm.cost_items || []).filter(c => parseFloat(c.cost_amount));
      // AP follows each supplier's own type (an airline on a package invoice is Airline
      // Ticket Payable), the same account its payments clear.
      const supTypes = await supplierTypesFor(rows.map(c => c.supplier_id));
      const currencyIds = [...new Set(rows.map(c => c.currency_id).filter(Boolean))];
      const { data: currs } = currencyIds.length
        ? await supabase.from('currencies').select('id, code').in('id', currencyIds)
        : { data: [] };
      const costItems = [];
      for (const c of rows) {
        const isInternal = c.cost_type === 'internal';
        const amount = parseFloat(c.cost_amount);
        if (isInternal && !invoice.costing_finalized) {
          costItems.push({ ...c, cost_amount: amount, journaled: false, journal_entry_id: null });
          continue;
        }
        const totalCost = c.per_pax ? amount * costPaxQty(c, items) : amount;
        const payable = costPayableAccount(supTypes[c.supplier_id], defaultPayable);
        if (!totalCost) { costItems.push({ ...c, cost_amount: amount, journaled: true, journal_entry_id: null }); continue; }
        const lineDesc = [num, invoice.clients?.full_name, c.notes || c.supplier_label].filter(Boolean).join(' — ');
        const costJe = await postJournal({
          date: entryDate,
          description: `${lineDesc} (admin re-edit)`,
          reference_type: refType,
          reference_id: id,
          currency_id: c.currency_id || null,
          exchange_rate: rateFor(currs?.find(x => x.id === c.currency_id)?.code, c.exchange_rate),
          branch_id: invoice.branch_id || null,
          created_by: username,
          lines: [
            { account_id: expense, debit_amount: totalCost, credit_amount: 0, description: lineDesc },
            isInternal || !c.supplier_id
              ? { account_id: isInternal ? ACCOUNTS.OTHER_PAYABLES : payable, debit_amount: 0, credit_amount: totalCost, description: lineDesc }
              : { account_id: payable, debit_amount: 0, credit_amount: totalCost, description: lineDesc, party_type: 'supplier', party_id: c.supplier_id },
          ],
        });
        costItems.push({ ...c, cost_amount: amount, journaled: true, journal_entry_id: costJe?.id || null });
      }

      const patch = {
        line_items: items,
        cost_items: costItems,
        due_date: editForm.due_date || null,
        notes: editForm.notes || null,
        issued_date: editForm.issued_date || invoice.issued_date,
        journal_entry_id: je.id,
        status: 'SENT',
      };
      await supabase.from('invoices').update(patch).eq('id', id);
      if (invoice.source_type === 'booking' && invoice.source_id && editForm.passengers?.length) {
        await supabase.from('bookings').update({ passengers: editForm.passengers }).eq('id', invoice.source_id);
      }
      setInvoice(f => ({ ...f, ...patch }));
      setAdminEditing(false);
    } catch (e) {
      alert('Admin edit failed: ' + (e.message || e));
      console.error(e);
    }
    setUpdating(false);
  }

  function addLineItem() {
    setEditForm(f => ({
      ...f,
      line_items: [...f.line_items, { description: '', sub: '', qty: 1, unitPrice: 0, amount: 0 }],
    }));
  }

  function removeLineItem(i) {
    setEditForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  }

  // Pick a passenger's pax type from the list instead of retyping the description —
  // updates the line's `type` and swaps the leading "Adult — …" of its description.
  // Amounts are untouched, so this alone never changes the ledger.
  function setLinePaxType(i, t) {
    setEditForm(f => ({
      ...f,
      line_items: f.line_items.map((it, idx) => {
        if (idx !== i) return it;
        const old = paxTypes.includes(lineTypeOf(it)) ? lineTypeOf(it) : '';
        const desc = it.description || '';
        const description = old && desc.startsWith(old) ? t + desc.slice(old.length) : (desc ? `${t} — ${desc}` : t);
        return { ...it, type: t, description };
      }),
    }));
  }

  function updateLineItem(i, field, value) {
    setEditForm(f => {
      const items = f.line_items.map((it, idx) => {
        if (idx !== i) return it;
        const updated = { ...it, [field]: value };
        if (field === 'qty' || field === 'unitPrice') {
          const qty = parseFloat(field === 'qty' ? value : it.qty) || 0;
          const price = parseFloat(field === 'unitPrice' ? value : it.unitPrice) || 0;
          updated.amount = qty * price;
        }
        return updated;
      });
      return { ...f, line_items: items };
    });
  }

  function handlePrint() {
    window.print();
  }

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  async function handleDownloadPdf() {
    setDownloadingPdf(true);
    try {
      const clientName = invoice.clients?.full_name || '';
      await downloadElementAsPdf(printRef.current, `${invoice.invoice_number}${clientName ? ' - ' + clientName : ''}`);
    } catch (e) {
      alert('Could not generate PDF: ' + (e.message || e));
    }
    setDownloadingPdf(false);
  }

  async function handleApplyDeposit(amount) {
    setApplyingDeposit(true);
    try {
      await applyClientDeposit(invoice.client_id, invoice.id, amount, username, `Deposit applied - ${invoice.invoice_number}`);
      setShowApplyDeposit(false);
      await load();
    } catch (e) {
      alert('Could not apply deposit: ' + (e.message || e));
    }
    setApplyingDeposit(false);
  }

  async function openRefund() {
    try { await promptPasscode(); } catch { return; }
    // Commission is a frozen snapshot at costing time, with no live link back to the
    // invoice total — a refund can't safely auto-adjust it, so just surface it as a
    // non-blocking notice (same dual-lookup pattern CommissionLedger.js uses: a
    // commission's reference_id can point at either the invoice or its source record).
    const { data: comms } = await supabase.from('commissions')
      .select('commission_amount, commission_status')
      .in('reference_id', [invoice.id, invoice.source_id].filter(Boolean))
      .in('commission_status', ['PENDING', 'APPROVED']);
    setRefundCommissionWarning(comms?.length ? comms.reduce((s, c) => s + (parseFloat(c.commission_amount) || 0), 0) : null);
    const paid = await getInvoicePaidAmount(id);
    setPaidToDate(paid);
    setRefundForm({ amount: '', reason: '' });
    setRefundError('');
    setEditing(false);
    setAdminEditing(false);
    setRefunding(true);
  }

  async function submitPartialRefund() {
    const amount = parseFloat(refundForm.amount) || 0;
    if (amount <= 0 || amount > total + 0.01) {
      return setRefundError(`Refund amount must be between 0 and the current total (LKR ${fmt(total)})`);
    }
    if (!refundForm.reason.trim()) {
      return setRefundError('A reason is required');
    }
    setUpdating(true);
    setRefundError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const revenueAcct = REVENUE_MAP[invoice.source_type] || ACCOUNTS.PACKAGE_REVENUE_LKR;

      // A small, standalone credit note — the original journal entries are never touched.
      // reference_type is deliberately NOT prefixed "invoice" — saveAdminEdit() finds
      // journals to void-and-repost via `.ilike('reference_type', 'invoice%')`, and a
      // string starting with "invoice" would get silently swept into that and lose its
      // own audit trail on a later Admin Edit. A full Void still catches this fine, since
      // that reverses everything by reference_id alone, no type check.
      const je = await postJournal({
        date: today,
        description: `Refund — ${invoice.invoice_number} — ${refundForm.reason}`,
        reference_type: 'CREDIT_NOTE',
        reference_id: id,
        branch_id: invoice.branch_id || null,
        created_by: username,
        lines: [
          { account_id: revenueAcct, debit_amount: amount, credit_amount: 0, description: 'Revenue refunded' },
          { account_id: ACCOUNTS.AR, debit_amount: 0, credit_amount: amount, description: 'AR reduced — refund', party_type: 'client', party_id: invoice.client_id },
        ],
      });

      // qty/unitPrice must be null, not 0 or '' — two places compute total pax by summing
      // qty across every line item (this file's own GP panel below, and InvoiceList.js's
      // calcGP) to scale any per_pax cost item; qty:1 here would silently inflate that
      // count. null contributes 0 via parseFloat(null)||0, while still resolving correctly
      // wherever total is computed via unit_price ?? unitPrice ?? amount (nullish
      // coalescing falls through to `amount`, and qty ?? quantity ?? 1 falls through to 1).
      const refundLine = {
        description: `Refund — ${refundForm.reason}`,
        sub: `Processed ${today} · JE ${je.entry_number}`,
        qty: null,
        unitPrice: null,
        amount: -amount,
      };
      const newLineItems = [...lineItems, refundLine];
      const newTotal = newLineItems.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

      const patch = { line_items: newLineItems };
      if (paidToDate >= newTotal - 0.01) patch.status = 'PAID';

      await supabase.from('invoices').update(patch).eq('id', id);
      setRefunding(false);
      await load();
    } catch (e) {
      setRefundError(e.message || 'Refund failed');
    }
    setUpdating(false);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading…</div>;
  if (!invoice) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Invoice not found.</div>;
  if (invoice.accessDenied) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>This invoice isn't for one of your clients.</div>;

  const lineItems = (invoice.line_items && invoice.line_items.length > 0)
    ? invoice.line_items
    : buildLineItems(invoice.source_type, sourceData);
  const total = lineItems.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const totalAdvance = advances.reduce((s, a) => s + (parseFloat(a.base_amount) || parseFloat(a.amount) || 0), 0);
  const balanceDue = total - totalAdvance;
  const client = invoice.clients;

  // GP calculation from saved cost_items
  const costItems = invoice.cost_items || [];
  const totalPax = lineItems.reduce((s, l) => s + (parseFloat(l.qty) || 0), 0) || 1;
  const totalCostLKR = costItems.reduce((s, c) => {
    const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
    return s + (c.per_pax ? base * costPaxQty(c, lineItems) : base);
  }, 0);
  const gpLKR = total - totalCostLKR;
  const gpPct = total > 0 ? (gpLKR / total * 100) : 0;
  // Per-line GP — each revenue line's own sell minus its own share of cost, same logic as
  // InvoiceNew.js's commission calculation: a per_pax cost item's rate applies per person,
  // a flat cost item's total splits evenly across all pax on the invoice.
  const perPassengerMargins = lineItems.map((item, lIdx) => {
    const qty = parseInt(item.qty) || 1;
    const sell = parseFloat(item.amount) || 0;
    const costShare = costItems.reduce((s, c) => {
      const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
      if (c.pax_type_filter) {
        if (lineTypeOf(item) !== c.pax_type_filter) return s;
        const matchQty = costPaxQty(c, lineItems);
        return s + (c.per_pax ? base * qty : (matchQty > 0 ? (base * qty) / matchQty : 0));
      }
      if (c.line_item_index !== '' && c.line_item_index != null) {
        if (parseInt(c.line_item_index) !== lIdx) return s;
        return s + (c.per_pax ? base * qty : base);
      }
      return s + (c.per_pax ? base * qty : (totalPax > 0 ? (base * qty) / totalPax : 0));
    }, 0);
    return { description: item.description, qty, sell, costShare, margin: sell - costShare };
  });
  // Show source document notes (e.g. booking inclusions) if invoice has no custom notes
  const sourceNotes = sourceData?.notes || null;
  const displayNotes = invoice.notes || sourceNotes;

  const STATUS_COLORS = { DRAFT: '#888', SENT: '#f07040', PAID: '#22c55e', VOID: '#ef4444' };

  return (
    <>
      {PasscodeModal}
      {/* Action bar — hidden when printing */}
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'center', padding: '12px 24px',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        {/* Go back in browser history, not a hardcoded route — this invoice can be opened
            from many places (Commission Ledger, Client/Supplier Ledger drill-downs, AR,
            not just the Invoices list), and Back should always return to wherever that was. */}
        <button className="btn btn-secondary" onClick={() => navigate(isLimitedAgent ? '/agent' : -1)}>← Back</button>
        <div style={{ flex: 1 }} />
        {!isLimitedAgent && invoice.status !== 'VOID' && (
          <button className="btn btn-secondary" onClick={() => { setEditing(e => !e); setAdminEditing(false); }} disabled={updating}>
            {editing ? 'Close Edit' : 'Edit'}
          </button>
        )}
        {!isLimitedAgent && invoice.status !== 'VOID' && (
          <button className="btn btn-secondary" onClick={handleAdminEdit} disabled={updating}
            style={{ background: 'rgba(230, 126, 34, 0.1)', borderColor: '#e67e22', color: '#e67e22' }}>
            Admin Edit
          </button>
        )}
        {!isLimitedAgent && invoice.status !== 'VOID' && (
          <button className="btn btn-secondary" onClick={reopenCosting} disabled={updating}
            style={{ background: 'rgba(180, 83, 9, 0.1)', borderColor: '#b45309', color: '#b45309' }}
            title="Admin passcode required — sends this invoice to Costing Pending so supplier costs can be added or corrected">
            Reopen for Costing
          </button>
        )}
        {!isLimitedAgent && invoice.status !== 'VOID' && invoice.invoice_type !== 'PROFORMA' && total > 0.01 && (
          <button className="btn btn-secondary" onClick={openRefund} disabled={updating}
            style={{ background: 'rgba(8,145,178,0.1)', borderColor: '#0891b2', color: '#0891b2' }}
            title="Partial refund — reverses only part of the revenue, keeping the rest (e.g. service/processing fees) as earned income">
            Refund
          </button>
        )}
        {!isLimitedAgent && invoice.status !== 'VOID' && (
          <button className="btn btn-danger" onClick={async () => {
            try { await promptPasscode(); } catch { return; }
            updateStatus('VOID');
          }} disabled={updating}>Void</button>
        )}

        <button className="btn btn-primary" onClick={handlePrint}>Print / Download PDF</button>
        <button className="btn btn-secondary" onClick={handleDownloadPdf} disabled={downloadingPdf}
          style={{ background: 'rgba(34,197,94,0.1)', borderColor: '#16a34a', color: '#16a34a' }}
          title="Download a named PDF file (Invoice # + Client Name) to save or share">
          {downloadingPdf ? 'Generating…' : '⬇ Save PDF'}
        </button>
      </div>

      {/* GP Summary panel — screen only, never prints, gp_margins permission required */}
      {costItems.length > 0 && canSee('gp_margins') && (
        <div className="no-print" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', padding: '12px 24px' }}>
          {perPassengerMargins.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>GP Per Passenger</div>
              <table style={{ fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#555', fontSize: 11 }}>
                    <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4, paddingRight: 24 }}>Passenger</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4, paddingRight: 16 }}>Sell</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4, paddingRight: 16 }}>Cost</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>GP</th>
                  </tr>
                </thead>
                <tbody>
                  {perPassengerMargins.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding: '2px 24px 2px 0' }}>{p.description || `Passenger ${i + 1}`}</td>
                      <td style={{ textAlign: 'right', paddingRight: 16 }}>{fmt(p.sell)}</td>
                      <td style={{ textAlign: 'right', paddingRight: 16 }}>{fmt(p.costShare)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: p.margin >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(p.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Margin Summary (Cumulative)</div>
            <div>
              <span style={{ fontSize: 11, color: '#555' }}>Total Sell </span>
              <span style={{ fontWeight: 700 }}>LKR {fmt(total)}</span>
            </div>
            <div style={{ color: '#aaa' }}>−</div>
            <div>
              <span style={{ fontSize: 11, color: '#555' }}>Total Cost </span>
              <span style={{ fontWeight: 700 }}>LKR {fmt(totalCostLKR)}</span>
              <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>
                ({costItems.length} supplier{costItems.length !== 1 ? 's' : ''})
              </span>
            </div>
            <div style={{ color: '#aaa' }}>=</div>
            <div>
              <span style={{ fontSize: 11, color: '#555' }}>GP </span>
              <span style={{ fontWeight: 700, fontSize: 16, color: gpLKR >= 0 ? '#16a34a' : '#dc2626' }}>
                LKR {fmt(gpLKR)}
              </span>
              <span style={{ fontSize: 12, color: gpLKR >= 0 ? '#16a34a' : '#dc2626', marginLeft: 6 }}>
                ({gpPct.toFixed(1)}%)
              </span>
            </div>
          </div>
          {costItems.map((c, i) => {
            const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
            const lkr = c.per_pax ? base * costPaxQty(c, lineItems) : base;
            return (
              <div key={i} style={{ fontSize: 12, color: '#555', marginTop: 4, paddingLeft: 100 }}>
                {c.supplier_label || 'Supplier'}: {fmt(parseFloat(c.cost_amount))}
                {parseFloat(c.exchange_rate) !== 1 && ` × ${c.exchange_rate}`}
                {c.per_pax && ` × ${costPaxQty(c, lineItems)} pax`}
                {` = LKR ${fmt(lkr)}`}
              </div>
            );
          })}
        </div>
      )}

      {/* Commission panel — screen only, never prints, same gp_margins gate as GP Summary
          since it's equally commercially-sensitive. Shows the frozen commission snapshot
          exactly as it was calculated/saved, not a live recalculation. */}
      {commissions.length > 0 && canSee('gp_margins') && (
        <div className="no-print" style={{ background: '#fefce8', borderBottom: '1px solid #fde68a', padding: '12px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Agent Commission</div>
          {commissions.map((c) => {
            const t = c.commission_tables;
            // Describe the actual rule that produced this amount, in priority order:
            // 1) a matched commission_tables row (rule_name + its own tier/fixed/per-unit shape)
            // 2) a flat commission_percent stored directly on the commission (older/simpler flows)
            // 3) whatever's left (gp_amount + units) when neither rule reference survived —
            //    this is a frozen snapshot with no live link back to the source rule, so a
            //    package-tiered commission from before commission_table_id existed has no
            //    better description available than its own recorded inputs.
            let ruleDesc;
            if (t) {
              const basisLabel = t.commission_basis === 'ON_SELL' ? 'Sell' : t.commission_basis === 'ON_COST' ? 'Cost' : 'GP';
              const tierRange = (t.gp_from != null || t.gp_to != null)
                ? ` (${basisLabel} ${fmt(parseFloat(t.gp_from || 0))}–${t.gp_to != null ? fmt(parseFloat(t.gp_to)) : '∞'})` : '';
              const rate = t.commission_percent != null ? `${t.commission_percent}% of ${basisLabel}`
                : t.fixed_amount != null ? `Fixed LKR ${fmt(parseFloat(t.fixed_amount))}`
                : t.amount_per_unit != null ? `LKR ${fmt(parseFloat(t.amount_per_unit))} × ${c.units || 0} unit${c.units === 1 ? '' : 's'}`
                : '—';
              ruleDesc = `"${t.rule_name || 'Rule'}"${tierRange} — ${rate}`;
            } else if (c.commission_percent != null) {
              const basisLabel = c.commission_basis === 'ON_SELL' ? 'Sell' : c.commission_basis === 'ON_COST' ? 'Cost' : 'GP';
              ruleDesc = `${c.commission_percent}% of ${basisLabel}`;
            } else if (c.units != null) {
              // No commission_tables reference survived on this row — reconstruct it the
              // same way the app's own package-tiered logic works: the tier is matched on
              // GP PER PAX (gp_amount / pax count), never on the invoice's total GP, then
              // that tier's fixed_amount is multiplied by pax count. The commission row's
              // own `units` field is NOT trustworthy for this — it can be stale/wrong (seen
              // live: units=1 stored on a 3-passenger invoice) — so pax count always comes
              // from the invoice's own line_items (totalPax, already computed above), the
              // one number guaranteed to match this actual invoice.
              const gpPerUnit = parseFloat(c.gp_amount || 0) / totalPax;
              const tier = packageGpTiers.find(t => gpPerUnit >= parseFloat(t.gp_from) && (t.gp_to == null || gpPerUnit < parseFloat(t.gp_to)));
              ruleDesc = tier
                ? `"${tier.rule_name}" — GP/pax LKR ${fmt(gpPerUnit)} → LKR ${fmt(parseFloat(tier.fixed_amount))}/pax × ${totalPax} pax`
                : `Per-pax tier — GP/pax LKR ${fmt(gpPerUnit)} × ${totalPax} pax (no matching tier found; original rule may have changed since)`;
            } else {
              ruleDesc = 'No rule reference saved at the time';
            }
            return (
              <div key={c.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{c.agents ? `${c.agents.tls_code} — ${c.agents.full_name}` : 'Agent'}</div>
                  <div><span style={{ fontSize: 11, color: '#555' }}>GP Basis </span><span style={{ fontWeight: 600 }}>LKR {fmt(parseFloat(c.gp_amount || 0))}</span></div>
                  <div style={{ color: '#aaa' }}>×</div>
                  <div><span style={{ fontSize: 11, color: '#555' }}>Rule </span><span style={{ fontWeight: 600 }}>{ruleDesc}</span></div>
                  <div style={{ color: '#aaa' }}>=</div>
                  <div>
                    <span style={{ fontSize: 11, color: '#555' }}>Commission </span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#92400e' }}>LKR {fmt(parseFloat(c.commission_amount || 0))}</span>
                  </div>
                  <span className={`badge ${c.commission_status === 'PAID' ? 'badge-green' : c.commission_status === 'APPROVED' ? 'badge-yellow' : 'badge-blue'}`}>{c.commission_status}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Unapplied client deposit — never auto-applied; staff must explicitly choose to */}
      {depositBalance > 0.01 && balanceDue > 0.01 && invoice.status !== 'VOID' && (
        <div className="no-print" style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#1e3a8a' }}>
            This client has <strong>LKR {fmt(depositBalance)}</strong> in unapplied deposits.
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowApplyDeposit(true)}>Apply to This Invoice</button>
        </div>
      )}
      {showApplyDeposit && (
        <ApplyDepositModal
          depositBalance={depositBalance}
          balanceDue={balanceDue}
          applying={applyingDeposit}
          onConfirm={handleApplyDeposit}
          onCancel={() => setShowApplyDeposit(false)}
        />
      )}

      {/* Edit panel */}
      {editing && (
        <div className="no-print" style={{
          background: '#fff8f5', borderBottom: '2px solid #f07040', padding: '20px 24px',
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#f07040', marginBottom: 4 }}>Edit Invoice</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
            Posted invoices are locked per accounting standards. Only non-financial fields may be changed.
            To correct amounts, client, date or currency — void this invoice and reissue.
          </div>

          {/* Allowed: Due Date + Notes */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Due Date</label>
              <input type="date" className="form-input" style={{ width: 180 }}
                value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 260 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Notes</label>
              <textarea className="form-input" rows={2} style={{ resize: 'vertical' }}
                value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes printed on invoice…" />
            </div>
          </div>

          {/* Allowed: Line item descriptions only (no amounts, no delete, no add) */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Line Item Descriptions</div>
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Description text only — amounts are locked.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fff4ef' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#f07040', fontSize: 11, textTransform: 'uppercase' }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#f07040', fontSize: 11, textTransform: 'uppercase' }}>Sub-line</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#f07040', fontSize: 11, textTransform: 'uppercase', width: 130 }}>Amount (locked)</th>
                </tr>
              </thead>
              <tbody>
                {editForm.line_items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0e0d8' }}>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13 }}
                        value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} />
                      {paxTypes.length > 0 && (
                        <select className="form-input" style={{ fontSize: 12, marginTop: 4, padding: '3px 6px' }}
                          value={paxTypes.includes(lineTypeOf(item)) ? lineTypeOf(item) : ''}
                          onChange={e => setLinePaxType(i, e.target.value)}>
                          <option value="" disabled>— Pax type —</option>
                          {paxTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 12 }}
                        value={item.sub || ''} onChange={e => updateLineItem(i, 'sub', e.target.value)}
                        placeholder="optional" />
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#999', whiteSpace: 'nowrap' }}>
                      {fmt(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Passenger ticket numbers — booking invoices only */}
          {invoice.source_type === 'booking' && editForm.passengers?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Passenger Ticket Numbers</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fff4ef' }}>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#f07040', fontSize: 11, textTransform: 'uppercase' }}>Passenger</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#f07040', fontSize: 11, textTransform: 'uppercase' }}>Ticket Number</th>
                  </tr>
                </thead>
                <tbody>
                  {editForm.passengers.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0e0d8' }}>
                      <td style={{ padding: '6px 10px', fontWeight: 500 }}>{p.name || `Passenger ${i + 1}`}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <input className="form-input" style={{ fontSize: 13, fontFamily: 'monospace' }}
                          value={p.ticket_number || ''}
                          placeholder="e.g. 2764819234567"
                          onChange={e => setEditForm(f => ({
                            ...f,
                            passengers: f.passengers.map((px, pi) => pi === i ? { ...px, ticket_number: e.target.value } : px),
                          }))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveEdit} disabled={updating}>
              {updating ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Admin Edit panel — full unlock */}
      {adminEditing && (
        <div className="no-print" style={{ background: '#fff8f0', borderBottom: '2px solid #e67e22', padding: '20px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#e67e22', marginBottom: 4 }}>Admin Edit — Full Unlock</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
            Previous journal entries have been voided. New entries will be posted on save.
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Issued Date</label>
              <input type="date" className="form-input" style={{ width: 180 }}
                value={editForm.issued_date || invoice.issued_date || ''}
                onChange={e => setEditForm(f => ({ ...f, issued_date: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Due Date</label>
              <input type="date" className="form-input" style={{ width: 180 }}
                value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 260 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Notes</label>
              <textarea className="form-input" rows={2} style={{ resize: 'vertical' }}
                value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes printed on invoice…" />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Line Items</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fff4ef' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase' }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase' }}>Sub-line</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 70 }}>Qty</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 130 }}>Unit Price</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 130 }}>Amount</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {editForm.line_items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0e0d8' }}>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13 }}
                        value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} />
                      {paxTypes.length > 0 && (
                        <select className="form-input" style={{ fontSize: 12, marginTop: 4, padding: '3px 6px' }}
                          value={paxTypes.includes(lineTypeOf(item)) ? lineTypeOf(item) : ''}
                          onChange={e => setLinePaxType(i, e.target.value)}>
                          <option value="" disabled>— Pax type —</option>
                          {paxTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 12 }}
                        value={item.sub || ''} onChange={e => updateLineItem(i, 'sub', e.target.value)}
                        placeholder="optional" />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13, textAlign: 'center', width: '100%' }} type="number"
                        value={item.qty} onChange={e => updateLineItem(i, 'qty', e.target.value)} />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13, textAlign: 'right', width: '100%' }} type="number"
                        value={item.unitPrice} onChange={e => updateLineItem(i, 'unitPrice', e.target.value)} />
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>
                      {fmt(item.amount)}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                      <button onClick={() => removeLineItem(i)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={addLineItem} style={{ marginTop: 8, background: 'none', border: '1px dashed #e67e22', color: '#e67e22', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>
              + Add Line
            </button>
            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, marginTop: 8, color: '#e67e22' }}>
              New Total: LKR {fmt(editForm.line_items.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Supplier Costs</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fff4ef' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase' }}>Supplier</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 150 }}>Cost Amount</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 110 }}>FX Rate</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 80 }}>Per Pax</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#e67e22', fontSize: 11, textTransform: 'uppercase', width: 130 }}>LKR Cost</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {(editForm.cost_items || []).map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0e0d8' }}>
                    <td style={{ padding: '6px 10px' }}>
                      {/* A search, not free text — typing a new name here used to change only the
                          label while supplier_id (which drives the AP ledger) stayed on the old
                          supplier, so costs "moved to IATA" kept posting to the airline. */}
                      {c.cost_type === 'internal' ? (
                        <input className="form-input" style={{ fontSize: 13 }} value={c.supplier_label || ''}
                          onChange={e => setEditForm(f => ({ ...f, cost_items: f.cost_items.map((x, xi) => xi === i ? { ...x, supplier_label: e.target.value } : x) }))} />
                      ) : (
                        <SupplierPicker row={c}
                          onChange={patch => setEditForm(f => ({ ...f, cost_items: f.cost_items.map((x, xi) => xi === i ? { ...x, ...patch } : x) }))} />
                      )}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13, textAlign: 'right', width: '100%' }} type="number" value={c.cost_amount || ''}
                        onChange={e => setEditForm(f => ({ ...f, cost_items: f.cost_items.map((x, xi) => xi === i ? { ...x, cost_amount: e.target.value } : x) }))} />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13, textAlign: 'right', width: '100%' }} type="number" value={c.exchange_rate || 1}
                        onChange={e => setEditForm(f => ({ ...f, cost_items: f.cost_items.map((x, xi) => xi === i ? { ...x, exchange_rate: e.target.value } : x) }))} />
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                      <input type="checkbox" checked={!!c.per_pax}
                        onChange={e => setEditForm(f => ({ ...f, cost_items: f.cost_items.map((x, xi) => xi === i ? { ...x, per_pax: e.target.checked } : x) }))}
                        style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>
                      {(() => {
                        const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
                        const lkr = c.per_pax ? base * costPaxQty(c, editForm.line_items) : base;
                        return fmt(lkr);
                      })()}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                      <button onClick={() => setEditForm(f => ({ ...f, cost_items: f.cost_items.filter((_, xi) => xi !== i) }))}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => setEditForm(f => ({ ...f, cost_items: [...(f.cost_items || []), { supplier_label: '', cost_amount: '', exchange_rate: 1 }] }))}
              style={{ marginTop: 8, background: 'none', border: '1px dashed #e67e22', color: '#e67e22', borderRadius: 6, padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>
              + Add Supplier Cost
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => { setAdminEditing(false); load(); }}>Cancel</button>
            <button onClick={saveAdminEdit} disabled={updating}
              style={{ background: '#e67e22', color: 'white', border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {updating ? 'Saving…' : 'Save & Re-post Journal'}
            </button>
          </div>
        </div>
      )}

      {/* Refund panel */}
      {refunding && (
        <div className="no-print" style={{ background: '#ecfeff', borderBottom: '2px solid #0891b2', padding: '20px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0891b2', marginBottom: 4 }}>Process Refund</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
            Posts a credit note for just the refunded amount — the rest of this invoice's revenue (e.g. service/processing fees you're keeping) stays posted as earned income. Does not touch the original journal entries.
          </div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 13 }}>
            <div><span style={{ color: '#888' }}>Current Total: </span><strong>LKR {fmt(total)}</strong></div>
            <div><span style={{ color: '#888' }}>Paid to Date: </span><strong>LKR {fmt(paidToDate)}</strong></div>
            <div><span style={{ color: '#888' }}>Outstanding: </span><strong>LKR {fmt(Math.max(total - paidToDate, 0))}</strong></div>
          </div>

          {refundCommissionWarning != null && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
              This invoice has an agent commission of LKR {fmt(refundCommissionWarning)} (pending or approved) that will <strong>not</strong> be automatically adjusted — review it manually in the Commission Ledger if the refund should reduce it.
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Refund Amount (LKR) *</label>
              <input className="form-input" type="number" step="0.01" style={{ width: 200, fontSize: 15, fontWeight: 600 }}
                value={refundForm.amount} max={total}
                onChange={e => setRefundForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 260 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Reason *</label>
              <textarea className="form-input" rows={2} style={{ resize: 'vertical' }}
                value={refundForm.reason} onChange={e => setRefundForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this being refunded? (e.g. cancelled — service fee and processing fee retained)" />
            </div>
          </div>

          {canSee('gp_margins') && costItems.length > 0 && (() => {
            const refundAmt = parseFloat(refundForm.amount) || 0;
            const projectedGp = gpLKR - refundAmt;
            return (
              <div style={{ marginBottom: 16, fontSize: 12, color: '#555' }}>
                Projected margin after this refund:{' '}
                <strong style={{ color: projectedGp >= 0 ? '#16a34a' : '#dc2626' }}>LKR {fmt(projectedGp)}</strong>
              </div>
            );
          })()}

          {refundError && <div style={{ marginBottom: 12, fontSize: 12, color: '#dc2626' }}>{refundError}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setRefunding(false)} disabled={updating}>Cancel</button>
            <button onClick={submitPartialRefund} disabled={updating || !refundForm.amount || !refundForm.reason.trim()}
              style={{ background: '#0891b2', color: 'white', border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {updating ? 'Posting…' : 'Post Refund'}
            </button>
          </div>
        </div>
      )}

      {/* Printable invoice */}
      <div ref={printRef} style={{
        maxWidth: 780, margin: '32px auto', padding: '40px 48px',
        background: 'white', boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
        fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#111',
        borderRadius: 8,
      }} className="invoice-print-area">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 36 }}>
          <div>
            <img src="/travelscape-logo-full.png" alt="Travelscape" style={{ height: 83, display: 'block', marginBottom: -14, marginLeft: -9 }} />
            <img src="/iata-logo.svg" alt="IATA" style={{ width: 92, height: 'auto', display: 'block', marginBottom: 2 }} />
            <div style={{ fontSize: 11, color: '#555', lineHeight: 1.7 }}>
              Travelscape Lanka Pvt Ltd<br />
              29/3 Colonel T.G Jayawardena Mawatha<br />
              Colombo 03, Sri Lanka
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#f07040', letterSpacing: -0.5 }}>
              {invoice.invoice_type === 'PROFORMA' ? 'PROFORMA INVOICE' : 'INVOICE'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{invoice.invoice_number}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
              <div>Issued: <strong>{invoice.issued_date}</strong></div>
              {invoice.due_date && <div>Due: <strong>{invoice.due_date}</strong></div>}
            </div>
            {invoice.invoice_type === 'PROFORMA' ? (
              <div style={{ marginTop: 8 }}>
                <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fff7ed', color: '#f07040', border: '1px solid #fed7aa' }}>PROFORMA</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '2px solid #f07040', marginBottom: 28 }} />

        {/* Bill To */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Bill To</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{client?.full_name || '—'}</div>
          {client?.phone && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{client.phone}</div>}
          {client?.email && <div style={{ fontSize: 12, color: '#555' }}>{client.email}</div>}
          {client?.address && <div style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-line' }}>{client.address}</div>}
        </div>

        {/* Line items */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#fff4ef' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</th>
              <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Unit Price</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 12px', fontSize: 13 }}>
                  <div style={{ fontWeight: 500 }}>{item.description}</div>
                  {item.sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'pre-line' }}>{item.sub}</div>}
                </td>
                <td style={{ textAlign: 'center', padding: '10px 12px', fontSize: 13 }}>{item.qty}</td>
                <td style={{ textAlign: 'right', padding: '10px 12px', fontSize: 13 }}>{fmt(item.unitPrice)}</td>
                <td style={{ textAlign: 'right', padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>{fmt(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Total */}
        {(() => {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
              <div style={{ width: 300 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8f8f8', borderRadius: '6px 6px 0 0', fontWeight: 600, fontSize: 14, borderBottom: '1px solid #eee' }}>
                  <span>Invoice Total</span>
                  <span>LKR {fmt(total)}</span>
                </div>
                {advances.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', fontSize: 13, color: '#555', borderBottom: '1px solid #eee' }}>
                    <span>Advance ({a.voucher_number} · {a.payment_mode})</span>
                    <span style={{ color: '#16a34a' }}>− {a.currencies?.code || 'LKR'} {fmt(parseFloat(a.amount))}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: advances.length ? (balanceDue <= 0 ? '#16a34a' : '#f07040') : '#f07040', color: 'white', borderRadius: advances.length ? '0 0 6px 6px' : 6, fontWeight: 700, fontSize: 15 }}>
                  <span>{advances.length ? 'Balance Due' : 'Total'}</span>
                  <span>LKR {fmt(Math.max(balanceDue, 0))}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Passenger list — show for booking invoices when passengers are recorded */}
        {invoice.source_type === 'booking' && sourceData?.passengers?.length > 0 && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Passengers</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8f8f8' }}>
                  <th style={{ textAlign: 'left', padding: '7px 10px', fontWeight: 700, color: '#555', borderBottom: '1px solid #eee', width: 36 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '7px 10px', fontWeight: 700, color: '#555', borderBottom: '1px solid #eee' }}>Passenger Name</th>
                  {invoice.invoice_type === 'INVOICE' && (
                    <th style={{ textAlign: 'left', padding: '7px 10px', fontWeight: 700, color: '#555', borderBottom: '1px solid #eee' }}>Ticket Number</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sourceData.passengers.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 10px', color: '#999' }}>{i + 1}</td>
                    <td style={{ padding: '6px 10px', fontWeight: 500 }}>{p.name || '—'}</td>
                    {invoice.invoice_type === 'INVOICE' && (
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: p.ticket_number ? '#111' : '#ccc' }}>
                        {p.ticket_number || 'TBC'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {displayNotes && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Notes</div>
            <div style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-line' }}>{displayNotes}</div>
          </div>
        )}

        {/* Payment Details */}
        <PaymentDetails style={{ marginTop: 24 }} />

        {/* Body closing note */}
        <div style={{ borderTop: '1px solid #eee', paddingTop: 14, marginTop: 24, marginBottom: 64, textAlign: 'center', fontSize: 11, color: '#aaa' }}>
          Thank you for choosing Travelscape Lanka (Pvt) Ltd. For queries, contact us at your usual channel.
        </div>

        <PrintFooter />
      </div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 8mm 10mm 28mm; }
          body { margin: 0; }
          div[style*="max-width: 780px"] { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: 100% !important; padding: 14px 18px !important; }
        }
      `}</style>
    </>
  );
}

function fmt(n) {
  if (n == null || isNaN(n)) return '0.00';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildLineItems(type, data) {
  if (!data) return [];

  if (type === 'booking') {
    const numPax = parseInt(data.num_pax) || 1;
    const sellPerPax = parseFloat(data.total_sell || 0) / numPax;
    const subParts = [
      data.packages?.package_code ? `Ref: ${data.packages.package_code}` : null,
      data.travel_date ? `Travel: ${data.travel_date.slice(0, 10)}` : null,
      data.return_date ? `Return: ${data.return_date.slice(0, 10)}` : null,
      data.booking_ref ? `Booking: ${data.booking_ref}` : null,
    ].filter(Boolean);
    return [{
      description: `${data.packages?.package_name || 'Package'} — Travel Package`,
      sub: subParts.length > 0 ? subParts.join(' · ') : null,
      qty: numPax,
      unitPrice: sellPerPax,
      amount: sellPerPax * numPax,
    }];
  }

  if (type === 'ticket') {
    const sell = parseFloat(data.sell_price || 0);
    return [{
      description: `Flight Ticket${data.airline ? ` — ${data.airline}` : ''}`,
      sub: [
        data.ticket_number ? `Ticket: ${data.ticket_number}` : null,
        data.pnr ? `PNR: ${data.pnr}` : null,
        data.route_from && data.route_to ? `Route: ${data.route_from} → ${data.route_to}` : null,
      ].filter(Boolean).join(' · '),
      qty: 1,
      unitPrice: sell,
      amount: sell,
    }];
  }

  if (type === 'visa') {
    const sell = parseFloat(data.sell_price || 0);
    return [{
      description: `Visa Processing${data.visa_types?.country ? ` — ${data.visa_types.country}` : ''}${data.visa_types?.name ? ` (${data.visa_types.name})` : ''}`,
      sub: data.clients?.full_name ? `Applicant: ${data.clients.full_name}` : null,
      qty: 1,
      unitPrice: sell,
      amount: sell,
    }];
  }

  return [];
}
